import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Envelope, ArrowClockwise, MagnifyingGlass, Paperclip, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const errMsg = (e) => e.response?.data?.detail || "Postfach nicht erreichbar";

const FolderList = ({ folders, current, onPick }) => (
  <div className="flex md:flex-col gap-1 overflow-x-auto md:w-48 shrink-0" data-testid="mail-folder-list">
    {folders.map((f) => (
      <button key={f.name} onClick={() => onPick(f.name)} data-testid={`mail-folder-${f.name}`}
        className={`text-left px-3 py-2 rounded-xl text-sm whitespace-nowrap transition-colors ${current === f.name ? "bg-primary text-white" : "hover:bg-muted"}`}>
        {f.label}
      </button>
    ))}
  </div>
);

const MessageDetail = ({ msg, folder, onBack }) => {
  const download = async (a) => {
    try {
      const r = await api.get(`/mail/messages/${msg.uid}/attachments/${a.index}`, { params: { folder }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Anhang konnte nicht geladen werden"); }
  };
  return (
    <div className="space-y-3" data-testid="mail-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="mail-detail-back-btn"><ArrowLeft size={16} className="mr-1" /> Zurück</Button>
      <h2 className="text-xl font-bold break-words" data-testid="mail-detail-subject">{msg.subject}</h2>
      <div className="text-sm text-muted-foreground space-y-0.5">
        <div><strong className="text-foreground">{msg.from.name}</strong> &lt;{msg.from.email}&gt;</div>
        <div className="break-all">An: {msg.to}{msg.cc ? ` · Cc: ${msg.cc}` : ""}</div>
        <div>{fmt(msg.date)}</div>
      </div>
      {msg.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="mail-attachments">
          {msg.attachments.map((a) => (
            <button key={a.index} onClick={() => download(a)} data-testid={`mail-attachment-${a.index}`}
              className="flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 hover:bg-muted">
              <DownloadSimple size={14} /> {a.filename} <span className="text-muted-foreground">({kb(a.size)})</span>
            </button>
          ))}
        </div>
      )}
      {msg.html
        ? <iframe title="Mail" sandbox="" srcDoc={msg.html} className="w-full min-h-[60vh] border rounded-xl bg-white" data-testid="mail-detail-html" />
        : <pre className="whitespace-pre-wrap text-sm font-sans border rounded-xl p-4 bg-white" data-testid="mail-detail-text">{msg.text}</pre>}
    </div>
  );
};

const MessageRow = ({ m, onOpen }) => (
  <button onClick={() => onOpen(m)} data-testid={`mail-row-${m.uid}`}
    className="w-full text-left px-3 py-2.5 border-b hover:bg-muted/60 flex gap-3 items-start transition-colors">
    <div className="flex-1 min-w-0">
      <div className={`text-sm truncate ${m.unread ? "font-bold" : ""}`}>{m.from.name || m.from.email}</div>
      <div className={`text-sm truncate ${m.unread ? "font-semibold" : "text-muted-foreground"}`}>{m.subject}</div>
    </div>
    <div className="flex flex-col items-end gap-1 shrink-0">
      <span className="text-xs text-muted-foreground">{fmt(m.date)}</span>
      {m.has_attachments && <Paperclip size={14} className="text-muted-foreground" />}
    </div>
  </button>
);

export default function EmailInbox() {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return <div className="msb-card text-sm" data-testid="mail-no-access">Kein Zugriff – der Posteingang ist nur für Admins.</div>;
  }
  return <Inbox />;
}

function Inbox() {
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/mail/messages", { params: { folder, page, q: query } })).data); }
    catch (e) { toast.error(errMsg(e)); setData({ messages: [], total: 0, page: 1, page_size: 30 }); }
    finally { setLoading(false); }
  }, [folder, page, query]);

  useEffect(() => { api.get("/mail/folders").then((r) => setFolders(r.data.folders)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (m) => {
    try { setDetail((await api.get(`/mail/messages/${m.uid}`, { params: { folder } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pick = (f) => { setFolder(f); setPage(1); setDetail(null); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4" data-testid="email-inbox-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-3">
          <Envelope size={40} weight="duotone" className="text-primary" /> E-Mail-Posteingang
        </h1>
        <p className="text-sm text-muted-foreground mt-1">info@mainspessartblick.de · nur Lesen</p>
      </div>
      <div className="msb-card flex flex-col md:flex-row gap-4">
        <FolderList folders={folders} current={folder} onPick={pick} />
        <div className="flex-1 min-w-0">
          {detail ? <MessageDetail msg={detail} folder={folder} onBack={() => setDetail(null)} /> : (
            <>
              <form className="flex gap-2 mb-3" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(q.trim()); }}>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen (Absender, Betreff, Text)…" data-testid="mail-search-input" />
                <Button type="submit" variant="outline" data-testid="mail-search-btn"><MagnifyingGlass size={16} /></Button>
                <Button type="button" variant="outline" onClick={load} disabled={loading} data-testid="mail-refresh-btn">
                  <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
                </Button>
              </form>
              <div className="text-xs text-muted-foreground mb-1" data-testid="mail-total">{data ? `${data.total} Nachrichten${query ? ` für „${query}"` : ""}` : "Lade…"}</div>
              <div className="border rounded-xl overflow-hidden" data-testid="mail-list">
                {(data?.messages || []).map((m) => <MessageRow key={m.uid} m={m} onOpen={open} />)}
                {data && !data.messages.length && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="mail-empty">Keine Nachrichten</div>}
              </div>
              {pages > 1 && (
                <div className="flex justify-between items-center mt-3 text-sm">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="mail-prev-page">Zurück</Button>
                  <span data-testid="mail-page-info">Seite {page} / {pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="mail-next-page">Weiter</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
